import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Globe, Layers, Building2, Users, KanbanSquare,
  Sparkles, BarChart3, Settings as SettingsIcon, LogOut, Moon, Sun, Search,
  Command as CommandIcon, ChevronDown, UserCog, Database, Mail, Compass, Menu, Inbox, Briefcase, Brain, ShieldCheck, Coins, Telescope, Lock, Bot, Upload,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import CommandPalette from "@/components/CommandPalette";
import LanguageToggle from "@/components/LanguageToggle";
import BrandMark from "@/components/BrandMark";
import NotificationBell from "@/components/NotificationBell";
import DemoOverlay from "@/components/DemoOverlay";

export default function AppLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const navigate = useNavigate();

  const isAdmin = (user?.role || "").toUpperCase() === "ADMIN";
  const [pendingCount, setPendingCount] = useState(0);
  const [replyCoachCount, setReplyCoachCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const { default: api } = await import("@/lib/api");
        const { data } = await api.get("/email/approvals/_count");
        if (alive) setPendingCount(data?.count || 0);
      } catch (_) { /* silent */ }
    };
    const fetchReplyCoach = async () => {
      try {
        const { default: api } = await import("@/lib/api");
        const { data } = await api.get("/reply-coach/pending");
        if (alive) setReplyCoachCount(data?.count || 0);
      } catch (_) { /* silent */ }
    };
    fetchCount(); fetchReplyCoach();
    const id = setInterval(() => { fetchCount(); fetchReplyCoach(); }, 30000); // refresh every 30s
    return () => { alive = false; clearInterval(id); };
  }, []);

  const NAV = [
    { to: "/", label: t("nav.dashboard"), icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/principals", label: t("nav.principals"), icon: Briefcase, testid: "nav-principals" },
    { to: "/companies", label: t("nav.companies"), icon: Building2, testid: "nav-companies" },
    { to: "/contacts", label: t("nav.contacts"), icon: Users, testid: "nav-contacts" },
    { to: "/pipeline", label: t("nav.pipeline"), icon: KanbanSquare, testid: "nav-pipeline" },
    { to: "/ai-agent", label: t("nav.ai_agent"), icon: Sparkles, testid: "nav-ai-agent" },
    { to: "/missions", label: t("nav.missions"), icon: Bot, testid: "nav-missions" },
    { to: "/discover", label: t("nav.discover"), icon: Compass, testid: "nav-discover" },
    { to: "/approvals", label: t("nav.approvals"), icon: Inbox, testid: "nav-approvals", badge: pendingCount },
    { to: "/email-performance", label: t("nav.email_perf"), icon: BarChart3, testid: "nav-email-perf" },
    { to: "/coach", label: t("nav.ai_coach"), icon: Brain, testid: "nav-ai-coach" },
    { to: "/reply-coach", label: t("nav.reply_coach", "Reply Coach"), icon: Sparkles, testid: "nav-reply-coach", badge: replyCoachCount },
    { to: "/quality-audit", label: t("nav.quality_audit"), icon: ShieldCheck, testid: "nav-quality-audit" },
    { to: "/market-intel", label: t("nav.market_intel"), icon: Telescope, testid: "nav-market-intel" },
    { to: "/reports", label: t("nav.reports"), icon: BarChart3, testid: "nav-reports" },
    { to: "/digest", label: t("nav.digest"), icon: Mail, testid: "nav-digest" },
  ];
  const META = [
    { to: "/countries", label: t("nav.countries"), icon: Globe, testid: "nav-countries" },
    { to: "/sectors", label: t("nav.sectors"), icon: Layers, testid: "nav-sectors" },
  ];
  const ADMIN_NAV = [
    { to: "/private-docs", label: t("nav.private_docs"), icon: Lock, testid: "nav-private-docs" },
    { to: "/users", label: t("nav.users"), icon: UserCog, testid: "nav-users" },
    { to: "/import-export", label: t("nav.import_export"), icon: Database, testid: "nav-import-export" },
    { to: "/import-mandants", label: "Import Mandants", icon: Upload, testid: "nav-import-mandants" },
    { to: "/data-prep", label: t("nav.data_prep"), icon: Database, testid: "nav-data-prep" },
    { to: "/credits", label: t("nav.api_credits"), icon: Coins, testid: "nav-api-credits" },
  ];

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex h-screen bg-background text-foreground">
      {/* Mobile Drawer */}
      <Sheet open={mobileNav} onOpenChange={setMobileNav}>
        <SheetContent side="left" className="w-72 p-0 md:hidden flex flex-col pt-[env(safe-area-inset-top)]" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="h-14 flex-shrink-0 flex items-center gap-2.5 px-5 border-b border-border">
            <BrandMark variant="compact" />
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto pb-[max(env(safe-area-inset-bottom),5rem)]">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setMobileNav(false)}
                       data-testid={`m-${item.testid}`}
                       className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
                <div className="sidebar-dot" />
                <item.icon className="h-[15px] w-[15px]" strokeWidth={1.6} />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <Badge variant="default" className="h-4 min-w-4 px-1 text-[10px] tabular-nums">{item.badge}</Badge>
                )}
              </NavLink>
            ))}
            {isAdmin && (
              <>
                <div className="pt-5 pb-1.5 px-3 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium">{t("nav.reference")}</div>
                {META.map((item) => (
                  <NavLink key={item.to} to={item.to} onClick={() => setMobileNav(false)}
                           className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
                    <div className="sidebar-dot" />
                    <item.icon className="h-[15px] w-[15px]" strokeWidth={1.6} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
                <div className="pt-5 pb-1.5 px-3 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium">{t("nav.administration")}</div>
                {ADMIN_NAV.map((item) => (
                  <NavLink key={item.to} to={item.to} onClick={() => setMobileNav(false)}
                           className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
                    <div className="sidebar-dot" />
                    <item.icon className="h-[15px] w-[15px]" strokeWidth={1.6} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </>
            )}
          </nav>
        </SheetContent>
      </Sheet>

      {/* Sidebar */}
      <aside className="hidden md:flex w-64 border-r border-border flex-col" data-testid="sidebar">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border">
          <BrandMark variant="compact" />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to} to={item.to} end={item.end}
              data-testid={item.testid}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <div className="sidebar-dot" />
              <item.icon className="h-[15px] w-[15px]" strokeWidth={1.6} />
              <span className="flex-1">{item.label}</span>
              {item.badge > 0 && (
                <Badge variant="default" className="h-4 min-w-4 px-1 text-[10px] tabular-nums" data-testid={`${item.testid}-badge`}>
                  {item.badge}
                </Badge>
              )}
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div className="pt-5 pb-1.5 px-3 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium">{t("nav.reference")}</div>
              {META.map((item) => (
                <NavLink
                  key={item.to} to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                >
                  <div className="sidebar-dot" />
                  <item.icon className="h-[15px] w-[15px]" strokeWidth={1.6} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
              <div className="pt-5 pb-1.5 px-3 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium">{t("nav.administration")}</div>
              {ADMIN_NAV.map((item) => (
                <NavLink
                  key={item.to} to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                >
                  <div className="sidebar-dot" />
                  <item.icon className="h-[15px] w-[15px]" strokeWidth={1.6} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="p-3 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-muted text-left"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-[11px] bg-primary/12 text-primary font-medium">
                    {(user?.name || user?.email || "?").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{user?.name || "—"}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
                </div>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56">
              <DropdownMenuLabel className="text-xs">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                <SettingsIcon className="h-4 w-4 mr-2" /> {t("nav.settings")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggle} data-testid="menu-theme-toggle">
                {theme === "dark" ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                {theme === "dark" ? t("nav.light_mode") : t("nav.dark_mode")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive" data-testid="menu-logout">
                <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 border-b border-border flex items-center px-4 md:px-8 gap-3 sticky top-0 bg-background/80 backdrop-blur z-30">
          <Button
            variant="ghost" size="sm"
            className="md:hidden h-9 w-9 p-0 shrink-0"
            onClick={() => setMobileNav(true)}
            data-testid="mobile-nav-trigger"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <button
            data-testid="cmdk-trigger"
            onClick={() => setPaletteOpen(true)}
            className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/40 hover:bg-muted border border-border rounded-md px-3 h-9 w-full max-w-md transition-colors"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{t("common.search")}…</span>
            <kbd className="ml-auto hidden sm:flex items-center gap-0.5 text-[10px] text-muted-foreground/80 font-mono">
              <CommandIcon className="h-3 w-3" />K
            </kbd>
          </button>
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
            <LanguageToggle />
            <Button
              variant="ghost" size="sm" onClick={toggle}
              data-testid="theme-toggle"
              className="h-9 w-9 p-0"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1600px] px-4 md:px-10 py-5 md:py-10 pb-[max(env(safe-area-inset-bottom),6rem)] md:pb-10 fade-up">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
      <DemoOverlay />
    </div>
  );
}
